import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useThemeClassName } from '../../contexts/ThemeContext';
import CircleCheck from '../icons/CircleCheck';
import CircleInvalid from '../icons/CircleInvalid';

import styles from '../../../styles/animations/texts-feedback.module.scss';

const propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
    valid: PropTypes.bool,
    validLabel: PropTypes.node,
    invalidLabel: PropTypes.node,
    topLabel: PropTypes.node,
    repeatCount: PropTypes.number,
    maxIteration: PropTypes.number,
    onAnimationComplete: PropTypes.func,
};

const defaultProps = {
    className: null,
    children: null,
    valid: null,
    validLabel: null,
    invalidLabel: null,
    topLabel: null,
    repeatCount: 14,
    maxIteration: 3,
    onAnimationComplete: null,
};

function TextsFeedback({
    className,
    children,
    valid,
    validLabel,
    invalidLabel,
    topLabel,
    repeatCount,
    maxIteration,
    onAnimationComplete,
}) {
    const [iteration, setIteration] = useState(0);

    const themeClassName = useThemeClassName(styles);

    const onAnimationIteration = useCallback(() => {
        const newIteration = iteration + 1;
        if (newIteration >= maxIteration && onAnimationComplete !== null) {
            onAnimationComplete();
        } else {
            setIteration(newIteration);
        }
    }, [iteration, maxIteration, setIteration, onAnimationComplete]);

    useEffect(() => {
        setIteration(0);
    }, [valid, setIteration]);

    const finalValidLabel = validLabel || (
        <FormattedMessage defaultMessage="Well played!" description="Feedback text" />
    );

    const finalInvalidLabel = invalidLabel || (
        <FormattedMessage defaultMessage="Nice try!" description="Feedback text" />
    );

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles.valid]: valid === true,
                    [styles.invalid]: valid === false,
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.content}>
                <div
                    className={classNames([
                        styles.top,
                        {
                            [styles.withLabel]: topLabel !== null,
                        },
                    ])}
                >
                    <div className={styles.label}>{topLabel}</div>
                    {valid === true ? <CircleCheck className={styles.icon} /> : null}
                    {valid === false ? <CircleInvalid className={styles.icon} /> : null}
                </div>
                {valid !== null ? (
                    <div className={styles.texts}>
                        {[...Array(repeatCount).keys()].map((index) => (
                            <div
                                key={`text-${index}`}
                                className={styles.text}
                                onAnimationIteration={
                                    repeatCount - 1 === index ? onAnimationIteration : null
                                }
                            >
                                {valid ? finalValidLabel : finalInvalidLabel}
                            </div>
                        ))}
                    </div>
                ) : null}
                {valid !== null ? <div className={styles.bottom} /> : null}
                {children}
            </div>
        </div>
    );
}

TextsFeedback.propTypes = propTypes;
TextsFeedback.defaultProps = defaultProps;

export default TextsFeedback;
